import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { formatCurrencyInCents } from '../../helpers/formatters';
import { publicPlanDisplayName } from '../../helpers/subscription';

const PROTECTED_PLAN_NAMES = new Set(['free', 'começando', 'comecando']);

function isProtectedPlan(plan) {
    const name = String(plan?.name || '').trim().toLowerCase();
    return PROTECTED_PLAN_NAMES.has(name);
}

function ListSubscriptionPlansPage() {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchItems = useCallback(() => {
        if (!isMaster) {
            return;
        }
        setIsLoading(true);
        api.get('subscription-plans')
            .then((response) => {
                setItems(response?.data?.data || []);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [isMaster]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems, listVersion]);

    const handleDelete = (item) => {
        if (isProtectedPlan(item)) {
            Swal.fire('Ação não permitida', 'Este plano não pode ser excluído.', 'info');
            return;
        }
        Swal.fire({
            title: 'Remover plano?',
            text: `${publicPlanDisplayName(item.name)} será removido permanentemente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? 'Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`subscription-plans/${item.uuid}`)
                    .then(() => {
                        setListVersion((version) => version + 1);
                        Swal.fire('Removido!', 'O plano foi removido com sucesso.', 'success');
                    })
                    .catch((error) => {
                        const message = error?.response?.data?.error || 'Houve um problema ao remover o plano.';
                        Swal.fire('Erro!', message, 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }
    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Planos de assinatura</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Planos de assinatura
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/admin/subscription-plans/new')}
                    startIcon={<Add />}
                    fullWidth={isMobile}
                >
                    Plano
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor mensal</TableCell>
                            <TableCell align="center">Features</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <TableRow key={item.uuid} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {publicPlanDisplayName(item.name)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {item.name}
                                    </Typography>
                                </TableCell>
                                <TableCell>{item.description || '—'}</TableCell>
                                <TableCell align="right">
                                    {formatCurrencyInCents(Number(item.monthly_billed_value) || 0)}
                                </TableCell>
                                <TableCell align="center">
                                    <Chip size="small" label={(item.features || []).length} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        aria-label="Editar plano"
                                        onClick={() => navigate(`/admin/subscription-plans/${item.uuid}/edit`)}
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    {!isProtectedPlan(item) ? (
                                        <IconButton
                                            aria-label="Excluir plano"
                                            color="error"
                                            onClick={() => handleDelete(item)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                        {items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Nenhum plano cadastrado.
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

export default ListSubscriptionPlansPage;
